/** @jsx jsx */
import { Button, Flex, FormGroup, FormLabel, Heading, Link, Stack } from "@eworkit/iris";
import { FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link as ReactRouterLink } from "react-router-dom";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { FormikForm } from "@/formikComponents";
import { Messenger } from "@/views/components/Messenger";
import { router } from "@/views/router";

export const validationSchema = Yup.object().shape({
  username: Yup.string().required(VALIDATION_TYPES.REQUIRED).email(VALIDATION_TYPES.EMAIL),
  password: Yup.string().required(VALIDATION_TYPES.REQUIRED),
});

export interface ILoginFormValues {
  username: string;
  password: string;
}

interface Props {
  form: FormikProps<ILoginFormValues>;
}

export const FORM_MESSENGER_CONTEXT = "login-form";

export const LoginForm: React.FC<Props> = React.memo(({ form }) => {
  const { t } = useTranslation("public");

  return (
    <Stack gap="md">
      <Heading size="h-md" align="center">
        {t("SESSION_LOGIN.LOGIN_FORM.HEADER")}
      </Heading>

      <FormikForm>
        <Stack gap="lg">
          <Stack gap="sm">
            <Messenger context={FORM_MESSENGER_CONTEXT} />

            <Stack gap="md">
              <FormGroup
                label={<FormLabel label={t("SESSION_LOGIN.LOGIN_FORM.PLACEHOLDER_USERNAME")} required />}
                error={<FormikError name="username" />}>
                <FormikTextInput size="lg" type="email" name="username" value={form.values.username} />
              </FormGroup>

              <FormGroup
                label={<FormLabel label={t("SESSION_LOGIN.LOGIN_FORM.PLACEHOLDER_PASSWORD")} required />}
                error={<FormikError name="password" data-test="error-username" />}>
                <FormikTextInput size="lg" type="password" name="password" value={form.values.password} />
              </FormGroup>
            </Stack>

            <Flex>
              <Link as={ReactRouterLink} variant="primary" to={router.get("password-reset").path}>
                {t("SESSION_LOGIN.BUTTON_FORGOT_PASSWORD")}
              </Link>
            </Flex>
          </Stack>

          <Button type="submit" disabled={form.isSubmitting} size="lg">
            {t("SESSION_LOGIN.LOGIN_FORM.BUTTON_SUBMIT")}
          </Button>
        </Stack>
      </FormikForm>
    </Stack>
  );
});
